import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";

export type Category = { name: string; color: string };

export const CATEGORY_COLORS = [
  // Blues
  "#0088FF", "#3B82F6", "#1D4ED8", "#0EA5E9", "#06B6D4", "#0E7490",
  // Purples & Violets
  "#8B5CF6", "#6366F1", "#7C3AED", "#A855F7", "#D946EF", "#9333EA",
  // Pinks & Reds
  "#EC4899", "#F43F5E", "#EF4444", "#DC2626", "#BE123C", "#E11D48",
  // Oranges & Yellows
  "#F97316", "#EA580C", "#F59E0B", "#D97706", "#EAB308", "#CA8A04",
  // Greens
  "#22C55E", "#16A34A", "#10B981", "#059669", "#14B8A6", "#0F766E",
  // Navies, Slates & Neutrals
  "#113163", "#1E3A5F", "#334155", "#475569", "#64748B", "#0F172A",
];

export const MAX_CATEGORIES = 12;

type SettingsState = {
  categories: Category[];
  loading: boolean;
  saving: boolean;
  error: string | null;
};

const initialState: SettingsState = {
  categories: [],
  loading: false,
  saving: false,
  error: null,
};

function normaliseCategory(item: any, i: number): Category | null {
  if (typeof item === "string") {
    const trimmed = item.trim();
    if (!trimmed) return null;
    return { name: trimmed, color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] };
  }
  // Guard against nested objects — extract the deepest string name
  let rawName = item?.name;
  while (rawName && typeof rawName === "object") rawName = rawName.name;
  const name = String(rawName || "").trim();
  if (!name) return null;
  const color =
    typeof item?.color === "string" && /^#[0-9A-Fa-f]{3,8}$/.test(item.color)
      ? item.color
      : CATEGORY_COLORS[i % CATEGORY_COLORS.length];
  return { name, color };
}

function normaliseCategories(raw: any[]): Category[] {
  return raw.map(normaliseCategory).filter((c): c is Category => c !== null);
}

export const fetchSettings = createAsyncThunk(
  "settings/fetchSettings",
  async (getAccessToken: (() => Promise<string>) | undefined, { rejectWithValue }) => {
    try {
      const data = await request(API_URLS.settingsCategories, {
        method: "GET",
        auth: true,
        ...(getAccessToken ? { getAccessToken } : {}),
      });
      return normaliseCategories(Array.isArray(data?.categories) ? data.categories : []);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to load settings");
    }
  }
);

export const saveCategories = createAsyncThunk(
  "settings/saveCategories",
  async (
    { categories, getAccessToken }: { categories: Category[]; getAccessToken?: () => Promise<string> },
    { rejectWithValue }
  ) => {
    try {
      const data = await request(API_URLS.settingsCategories, {
        method: "PUT",
        auth: true,
        body: { categories },
        ...(getAccessToken ? { getAccessToken } : {}),
      });
      return normaliseCategories(Array.isArray(data?.categories) ? data.categories : categories);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to save categories");
    }
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearSettingsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load settings";
      })
      .addCase(saveCategories.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveCategories.fulfilled, (state, action) => {
        state.saving = false;
        state.categories = action.payload;
      })
      .addCase(saveCategories.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) || "Failed to save categories";
      });
  },
});

export const { clearSettingsError } = settingsSlice.actions;
export default settingsSlice.reducer;
